import { propState, type Command } from "../document/commands.ts";
import type { MapDocument, WorldDocument } from "../document/types.ts";
import { anchoredIn, assertAreaRect, shiftedRect, type AreaGrid, type AreaOffset, type AreaRect } from "./area.ts";
import type { AreaThing } from "./area-contents.ts";
import { gridCommand, isGridLayer, paintedIn, planGrid, rawGridOf } from "./area-grids.ts";

/**
 * The move of an area (ADR-128 sections C, C2 and D): one `compound` of the commands that exist, built against the
 * document as it stands when the builder confirms. One `setTiles`, `paintCorners`, `paintCollision`, `paintZone` or
 * `paintDoor` for each grid layer that changes, carrying only the cells whose value changes, each written once with
 * its final value over the union of the source and the destination; then one `updateProp` for each prop and one
 * `updateEntity` for each marker anchored inside, moved by the offset with its id and everything else kept. Layers
 * follow the map's order and items their layer's, so equal inputs give equal commands. No new command kind, intent
 * or version: `commandToOps` carries the compound as its parts.
 *
 * A destination not wholly inside the map is refused (decision 24). Paint at the destination is replaced, never
 * refused (decisions 19 and 20, lifting decision 54): each grid cell of the destination ends holding what the
 * matching source cell held, empties included, and the plan counts the painted cells the move changes, less what
 * the move itself empties, by layer, so the bar can say "Replaces 1,204 painted cells on 3 layers" before Enter.
 * Props and markers already at the destination are never moved or removed by a move (decisions 19 and 55): they
 * are named, on every layer of the map, so the bar can count and outline them, each marked with whether its layer
 * is one the move takes.
 */

/**
 * How far a destination would leave the map on each side, in tiles, 0 for a side it stays within.
 */
export interface AreaEdge {
  readonly west: number;
  readonly east: number;
  readonly north: number;
  readonly south: number;
}

/**
 * The painted destination cells a move replaces on one taken layer: the layer, its grid and the cells, by index
 * in that grid, in index order.
 */
export interface AreaReplaced {
  readonly layerId: string;
  readonly grid: AreaGrid;
  readonly indices: readonly number[];
}

/**
 * A prop or a marker that stands at the destination and stays, and whether its layer is one the move takes. On a
 * layer out of reach (locked or hidden; `areaReach` says which) nothing moves, so a thing standing where the
 * source and the destination overlap is named too.
 */
export interface AreaKeptThing extends AreaThing {
  readonly taken: boolean;
}

/**
 * The props and markers that stand at the destination and stay (decisions 19 and 55), on any layer of the map, in
 * the map's order and each layer's own.
 */
export interface AreaKept {
  readonly props: readonly AreaKeptThing[];
  readonly markers: readonly AreaKeptThing[];
}

/**
 * A planned move: nothing to do (decision 29); refused at the map's edge, with how far; or the command with the
 * layers it writes in the map's order, the cell changes it carries, the props and markers it moves, the painted
 * destination cells it replaces and the things it leaves standing at the destination.
 */
export type AreaMovePlan =
  | { readonly kind: "nothing" }
  | { readonly kind: "refused"; readonly edge: AreaEdge }
  | { readonly kind: "move"; readonly command: Extract<Command, { readonly kind: "compound" }>; readonly layers: readonly string[]; readonly changes: number; readonly props: number; readonly markers: number; readonly replaced: readonly AreaReplaced[]; readonly kept: AreaKept };

/**
 * A map of a world by id.
 *
 * @param world The world.
 * @param mapId The map.
 * @returns The map.
 * @throws Error when the world has no such map.
 */
export function areaMapOf(world: WorldDocument, mapId: string): MapDocument {
  const map = world.maps.find((m) => m.id === mapId);
  if (map === undefined) {
    throw new Error(`map ${mapId} does not exist`);
  }
  return map;
}

/**
 * How far a rectangle would leave a map on each side.
 *
 * @param map The map.
 * @param rect The rectangle of tiles.
 * @returns The distances, or null when it lies wholly inside.
 */
export function edgeOf(map: MapDocument, rect: AreaRect): AreaEdge | null {
  const edge = { west: Math.max(0, -rect.x0), east: Math.max(0, rect.x1 - (map.width - 1)), north: Math.max(0, -rect.y0), south: Math.max(0, rect.y1 - (map.height - 1)) };
  return edge.west + edge.east + edge.north + edge.south === 0 ? null : edge;
}

/**
 * Whether the taken layers hold anything inside a rectangle.
 *
 * @param map The map.
 * @param layerIds The layers taken.
 * @param rect The rectangle of tiles.
 * @returns True when a taken layer paints a cell or anchors a thing there.
 */
export function areaHolds(map: MapDocument, layerIds: ReadonlySet<string>, rect: AreaRect): boolean {
  return map.layers.some((layer) => {
    if (!layerIds.has(layer.id)) {
      return false;
    }
    if (layer.kind === "prop") {
      return layer.props.some((p) => anchoredIn(rect, p.x, p.y));
    }
    if (layer.kind === "entity") {
      return layer.entities.some((e) => anchoredIn(rect, e.x, e.y));
    }
    return paintedIn(map, layer, rect) > 0;
  });
}

/**
 * The props and markers that stand at the destination and are not moved: on a taken layer those anchored there
 * and not in the source, on any other layer every one anchored there.
 *
 * @param map The map.
 * @param layerIds The layers taken.
 * @param rect The source rectangle.
 * @param destination The destination rectangle.
 * @returns The things, in layer order.
 */
function keptAt(map: MapDocument, layerIds: ReadonlySet<string>, rect: AreaRect, destination: AreaRect): AreaKept {
  const props: AreaKeptThing[] = [];
  const markers: AreaKeptThing[] = [];
  for (const layer of map.layers) {
    const taken = layerIds.has(layer.id);
    /**
     * Whether a thing of this layer stands at the destination and stays.
     *
     * @param thing The prop or marker.
     * @returns True when it is kept.
     */
    const stays = (thing: { readonly x: number; readonly y: number }): boolean => anchoredIn(destination, thing.x, thing.y) && !(taken && anchoredIn(rect, thing.x, thing.y));
    if (layer.kind === "prop") {
      props.push(...layer.props.filter(stays).map((p) => ({ layerId: layer.id, id: p.id, taken })));
    } else if (layer.kind === "entity") {
      markers.push(...layer.entities.filter(stays).map((e) => ({ layerId: layer.id, id: e.id, taken })));
    }
  }
  return { props, markers };
}

/**
 * Plans the move of an area.
 *
 * @param world The world as it stands when the move is confirmed.
 * @param mapId The map in view.
 * @param rect The area's rectangle of tiles.
 * @param offset The offset in whole tiles.
 * @param layerIds The layers the reach takes (`areaReach`); only these are read or written.
 * @returns The plan.
 * @throws Error when the map is missing or a zone or door byte names nothing; RangeError when the rectangle is not
 *   one of whole tiles inside the map or the offset is not in whole tiles.
 */
export function planAreaMove(world: WorldDocument, mapId: string, rect: AreaRect, offset: AreaOffset, layerIds: readonly string[]): AreaMovePlan {
  const map = areaMapOf(world, mapId);
  assertAreaRect(map, rect);
  if (!Number.isInteger(offset.dx) || !Number.isInteger(offset.dy)) {
    throw new RangeError(`a move's offset is in whole tiles, not ${offset.dx}, ${offset.dy}`);
  }
  const taken = new Set(layerIds);
  if ((offset.dx === 0 && offset.dy === 0) || !areaHolds(map, taken, rect)) {
    return { kind: "nothing" };
  }
  const destination = shiftedRect(rect, offset);
  const edge = edgeOf(map, destination);
  if (edge !== null) {
    return { kind: "refused", edge };
  }
  const kept = keptAt(map, taken, rect, destination);
  const grids: Command[] = [];
  const items: Command[] = [];
  const replaced: AreaReplaced[] = [];
  const written: string[] = [];
  let changes = 0;
  let props = 0;
  let markers = 0;
  for (const layer of map.layers) {
    if (!taken.has(layer.id)) {
      continue;
    }
    const before = grids.length + items.length;
    if (layer.kind === "prop") {
      for (const prop of layer.props.filter((p) => anchoredIn(rect, p.x, p.y))) {
        const state = propState(prop);
        items.push({ kind: "updateProp", mapId, layerId: layer.id, propId: prop.id, before: state, after: { ...state, x: prop.x + offset.dx, y: prop.y + offset.dy } });
        props += 1;
      }
    } else if (layer.kind === "entity") {
      for (const entity of layer.entities.filter((e) => anchoredIn(rect, e.x, e.y))) {
        items.push({ kind: "updateEntity", mapId, layerId: layer.id, entityId: entity.id, before: { x: entity.x, y: entity.y, fields: entity.fields }, after: { x: entity.x + offset.dx, y: entity.y + offset.dy, fields: entity.fields } });
        markers += 1;
      }
    } else if (isGridLayer(layer)) {
      const plan = planGrid(map, layer, rect, offset);
      if (plan.replaced.length > 0) {
        replaced.push({ layerId: layer.id, grid: rawGridOf(layer).grid, indices: plan.replaced });
      }
      if (plan.changes.length > 0) {
        grids.push(gridCommand(mapId, layer, plan.changes, world.tilesets));
        changes += plan.changes.length;
      }
    }
    if (grids.length + items.length > before) {
      written.push(layer.id);
    }
  }
  if (grids.length + items.length === 0) {
    return { kind: "nothing" };
  }
  return { kind: "move", command: { kind: "compound", commands: [...grids, ...items] }, layers: written, changes, props, markers, replaced, kept };
}
